import { diffProjects } from './diff';
import type { Project, Revision } from './types';

/*
 * Page revision marking (EPLAN "revision management"): a named revision is
 * compared with the previous one; every page that changed carries the new
 * revision in its title block and is listed in the revision list.
 */

/** Pages (ids) that differ between two states of a project; all pages when there is no earlier state. */
export function changedPages(previous: Project | null, current: Project): string[] {
  if (!previous) return current.pages.map((p) => p.id);
  const out = new Set<string>(diffProjects(previous, current).touched.keys());
  const before = new Map(previous.pages.map((p) => [p.id, p]));
  for (const p of current.pages) {
    const o = before.get(p.id);
    if (!o || o.desc !== p.desc || o.fn !== p.fn || o.loc !== p.loc || o.site !== p.site || o.ud !== p.ud || JSON.stringify(o.layout ?? null) !== JSON.stringify(p.layout ?? null)) out.add(p.id);
  }
  return current.pages.map((p) => p.id).filter((id) => out.has(id));
}

/** Stamps a named revision: marks the changed pages and adds the revision to the history. */
export function stampRevision(pr: Project, previous: Project | null, rev: Revision): Project {
  const pages = changedPages(previous, pr);
  const set = new Set(pages);
  for (const p of pr.pages) if (set.has(p.id)) p.rev = rev.label;
  const entry: Revision = { ...rev, pages };
  pr.revisions = [...(pr.revisions ?? []).filter((r) => r.label !== rev.label), entry];
  pr.revision = { label: rev.label, date: rev.date, ...(rev.note ? { note: rev.note } : {}), ...(rev.by ? { by: rev.by } : {}) };
  return pr;
}

/** Revision shown in a page's title block: the one that last changed the page. */
export function pageRevision(pr: Project, pageIndex: number): Revision | undefined {
  const label = pr.pages[pageIndex]?.rev;
  if (!label) return undefined;
  return pr.revisions?.find((r) => r.label === label) ?? (pr.revision?.label === label ? pr.revision : undefined);
}
